import type { SelectItem } from '@vrplatform/components';
import { useAppName } from '@vrplatform/theme';
import { formatPaymentLineType } from '@vrplatform/ui-common';
import { type Maybe, sortBy } from '@vrplatform/utils';
import { useMemo } from 'react';
import { fetchApiAllItems } from '../api';
import type { ResponseData } from '../api';
import { useApiQuery } from '../api';
import { useTeam } from '../hooks/team';

type LineMappingsType = 'reservation' | 'transaction';

type ReservationLineMapping =
  ResponseData<'get:/reservations/line-mappings'>['data'][number];
type TransactionLineMapping =
  ResponseData<'get:/transactions/line-mappings'>['data'][number];

type LineMappingByType = {
  reservation: ReservationLineMapping;
  transaction: TransactionLineMapping;
};

type UseLineMappingsQueryArgs<TType extends LineMappingsType> = {
  type: TType;
  search?: string;
  appId?: string;
  page?: number;
  skip?: boolean;
  refetchOnMount?: boolean;
};

const DEFAULT_LIMIT = 250;
const DEFAULT_PAGE = 1;

const isVrtReservationLineType = (source: string | null | undefined) =>
  source === 'manual';

export const getLineTypeAppId = (lineType: string | null | undefined) => {
  const value = lineType?.trim();
  if (!value || !value.includes('_')) return null;

  const [appId] = value.split('_');
  return appId || null;
};

export function useLineMappingsQuery<TType extends LineMappingsType>({
  refetchOnMount,
  skip,
  ...args
}: UseLineMappingsQueryArgs<TType>) {
  const [{ id: teamId }] = useTeam();

  return useApiQuery(
    async (api, signal, variables) => {
      if (variables.type === 'reservation') {
        return (await fetchApiAllItems(async (page) => {
          const result = await api.GET('/reservations/line-mappings', {
            params: {
              query: {
                search: variables.search,
                appId: variables.appId,
                limit: DEFAULT_LIMIT,
                page,
              },
            },
            signal,
          });

          if (result.error) throw result.error;

          return {
            items: result.data.data,
            hasNextPage: !!result.data.pagination.nextPage,
          };
        }, variables.page)) satisfies LineMappingByType[TType][];
      }

      return (await fetchApiAllItems(async (page) => {
        const result = await api.GET('/transactions/line-mappings', {
          params: {
            query: {
              search: variables.search,
              appId: variables.appId,
              limit: DEFAULT_LIMIT,
              page,
            },
          },
          signal,
        });

        if (result.error) throw result.error;

        return {
          items: result.data.data,
          hasNextPage: !!result.data.pagination.nextPage,
        };
      }, variables.page)) as LineMappingByType[TType][];
    },
    {
      queryGroup: 'accounts',
      uniqueQueryKey: 'line-mappings-query',
      hideNotification: true,
      skip: !teamId || !!skip,
      refetchOnMount,
      variables: {
        ...args,
        search: args.search?.trim() || undefined,
        appId: args.appId?.trim() || undefined,
        page: args.page ?? DEFAULT_PAGE,
      },
    }
  );
}

export function useReservationLineMappingsInputOptions({
  reservation,
  showSourceGroups = true,
  ...props
}: Omit<UseLineMappingsQueryArgs<'reservation'>, 'type'> & {
  reservation: {
    bookingPlatform: Maybe<string>;
  };
  showSourceGroups?: boolean;
}) {
  const { appName } = useAppName();

  const { data: lineTypes = [], ...queryData } = useLineMappingsQuery({
    type: 'reservation',
    ...props,
  });

  const options = useMemo<SelectItem[]>(() => {
    const excludedGroupKey = 'Excluded mappings';

    const items = (lineTypes || []).map((lineType) => {
      const { app, line } = formatPaymentLineType(lineType);

      const bookingChannelMapping = reservation.bookingPlatform
        ? lineType.channelMappings.find(
            (channelMapping) =>
              channelMapping.bookingChannel === reservation.bookingPlatform
          )
        : undefined;

      const status = bookingChannelMapping?.status ?? lineType.status;

      const isExcluded = status === 'excluded';
      const isUnmapped = status === 'unmapped';
      const isVrt = isVrtReservationLineType(lineType.source);
      const disabled = isExcluded || isUnmapped;

      const sourceGroup = isVrt ? appName : app || 'External';
      const group = disabled
        ? excludedGroupKey
        : showSourceGroups
          ? sourceGroup
          : undefined;

      return {
        label: line,
        value: lineType.type,
        group,
        disabled,
        description: isExcluded
          ? 'This mapping is excluded'
          : isUnmapped
            ? 'Please add an account mapping'
            : undefined,
      };
    });

    if (!showSourceGroups) {
      const enabled = sortBy(
        items.filter((item) => item.group !== excludedGroupKey),
        'label'
      );
      const disabled = sortBy(
        items.filter((item) => item.group === excludedGroupKey),
        'label'
      );
      return [...enabled, ...disabled];
    }

    return sortBy(items, (item) => {
      if (item.group === excludedGroupKey) {
        return `99-${item.group}-${item.label}`;
      }

      if (item.group === appName) {
        return `01-${item.group}-${item.label}`;
      }

      return `02-${item.group || ''}-${item.label}`;
    });
  }, [lineTypes, reservation.bookingPlatform, appName, showSourceGroups]);

  return {
    data: options,
    originalData: lineTypes,
    ...queryData,
  };
}
